import React, { useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money, PAYMENT_METHODS } from '@/lib/helpers';
import { Plus, Truck, Pencil, GitMerge, Loader2 } from 'lucide-react';
import { TagCombobox } from '@/components/TagCombobox';

export default function Suppliers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: '', contact: '', phone: '' });
  const [pay, setPay] = useState({ amount: '', method: 'CASH' });
  const [renameOf, setRenameOf] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: async () => (await api.get('/suppliers')).data.data });

  const invalidateCatalog = () => {
    ['suppliers', 'catalog-tags', 'categories', 'catalog-duplicates'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const doRename = async () => {
    const nn = renameVal.trim();
    if (!nn) { toast.error('Enter a new name.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/catalog/rename', { old_name: renameOf.name, new_name: nn });
      toast.success(`Renamed to "${nn}" · ${r.data.data.garments_updated} items updated`);
      setRenameOf(null); invalidateCatalog();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const add = async () => {
    if (!form.name.trim()) { toast.error('Enter supplier name.'); return; }
    try { await api.post('/suppliers', form); toast.success('Supplier added'); setOpen(false); setForm({ name: '', contact: '', phone: '' }); qc.invalidateQueries({ queryKey: ['suppliers'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const recordPay = async () => {
    if (!pay.amount) return;
    try { await api.post(`/suppliers/${active.id}/payment`, { amount: Number(pay.amount), method: pay.method }); toast.success('Payment recorded'); setPayOpen(false); setPay({ amount: '', method: 'CASH' }); qc.invalidateQueries({ queryKey: ['suppliers'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Page>
      <TopBar title="Suppliers" showStore={false} right={<div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" className="h-9 rounded-full" onClick={() => setMergeOpen(true)} data-testid="catalog-merge-open"><GitMerge className="mr-1 h-4 w-4" />Merge</Button>
        <Button size="sm" className="h-9 rounded-full" onClick={() => setOpen(true)} data-testid="supplier-add"><Plus className="h-4 w-4" /></Button>
      </div>} />
      <div className="px-4 pt-4 space-y-2">
        {suppliers.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No suppliers yet.</p> :
          suppliers.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-3" data-testid={`supplier-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary"><Truck className="h-4 w-4" /></span>
                  <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.phone || s.contact || '—'}</p></div></div>
                <div className="flex items-center gap-2">
                  <div className="text-right"><p className="font-mono text-sm">{money(s.total_purchases)}</p>{s.outstanding > 0 && <p className="text-xs text-rose-700">Due {money(s.outstanding)}</p>}</div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => { setRenameOf(s); setRenameVal(s.name); }} data-testid={`supplier-rename-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                </div>
              </div>
              {s.outstanding > 0 && <Button size="sm" variant="secondary" className="mt-2 h-8 w-full rounded-lg" onClick={() => { setActive(s); setPayOpen(true); }} data-testid={`supplier-pay-${s.id}`}>Record payment</Button>}
            </div>
          ))}
      </div>
      <Drawer open={open} onOpenChange={setOpen}><DrawerContent className="bg-card"><div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" /><DrawerHeader><DrawerTitle>Add supplier</DrawerTitle></DrawerHeader>
        <div className="space-y-3 px-4 pb-6">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="supplier-form-name" /></div>
          <div><Label>Contact / City</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          <Button className="h-12 w-full rounded-xl" onClick={add} data-testid="supplier-form-save">Save</Button>
        </div></DrawerContent></Drawer>
      <Drawer open={payOpen} onOpenChange={setPayOpen}><DrawerContent className="bg-card"><div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" /><DrawerHeader><DrawerTitle>Record payment · {active?.name}</DrawerTitle></DrawerHeader>
        <div className="space-y-3 px-4 pb-6">
          <div><Label>Amount</Label><Input inputMode="numeric" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="supplier-pay-amount" /></div>
          <div><Label>Method</Label><Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}><SelectTrigger className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.filter((m) => m !== 'CREDIT').map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent></Select></div>
          <Button className="h-12 w-full rounded-xl" onClick={recordPay} data-testid="supplier-pay-save">Record</Button>
        </div></DrawerContent></Drawer>

      <Drawer open={!!renameOf} onOpenChange={(o) => { if (!o) setRenameOf(null); }}>
        <DrawerContent className="bg-card" data-testid="rename-drawer"><div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Rename &ldquo;{renameOf?.name}&rdquo;</DrawerTitle></DrawerHeader>
          <div className="space-y-3 px-4 pb-6">
            <p className="text-xs text-muted-foreground">Updates this name everywhere — the supplier, the matching category, and every linked item. If the new name already exists, the two are merged.</p>
            <div><Label>New name</Label><Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="rename-input" /></div>
            <Button className="h-12 w-full rounded-xl" onClick={doRename} disabled={busy} data-testid="rename-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rename everywhere'}</Button>
          </div>
        </DrawerContent>
      </Drawer>

      <MergeDuplicatesDrawer open={mergeOpen} onOpenChange={setMergeOpen} onDone={invalidateCatalog} />
    </Page>
  );
}

function MergeDuplicatesDrawer({ open, onOpenChange, onDone }) {
  const [targets, setTargets] = useState({});
  const [busyIdx, setBusyIdx] = useState(null);
  const [mSel, setMSel] = useState([]);
  const [mParent, setMParent] = useState('');
  const [mQuery, setMQuery] = useState('');
  const [mBusy, setMBusy] = useState(false);
  const { data: groups = [], isLoading, refetch } = useQuery({
    queryKey: ['catalog-duplicates'],
    enabled: open,
    queryFn: async () => (await api.get('/catalog/duplicates')).data.data,
  });
  const { data: allTags = [], refetch: refetchTags } = useQuery({
    queryKey: ['catalog-tags'],
    enabled: open,
    queryFn: async () => (await api.get('/catalog/tags')).data.data,
  });

  const merge = async (group, idx) => {
    const target = targets[idx] || group.suggested;
    const sources = group.names.map((n) => n.name).filter((n) => n !== target);
    if (!sources.length) { toast.error('Pick a different name to keep.'); return; }
    setBusyIdx(idx);
    try {
      const r = await api.post('/catalog/merge', { target, sources });
      toast.success(`Merged into "${target}" · ${r.data.data.garments_updated} items updated`);
      await refetch();
      onDone && onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyIdx(null); }
  };

  const toggleSel = (name) => setMSel((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const mFiltered = mQuery.trim() ? allTags.filter((t) => t.name.toLowerCase().includes(mQuery.trim().toLowerCase())) : allTags;
  const manualMerge = async () => {
    const parent = (mParent || '').trim();
    if (!parent) { toast.error('Choose a parent category to merge into.'); return; }
    const sources = mSel.filter((n) => n !== parent);
    if (!sources.length) { toast.error('Select at least one category to merge into the parent.'); return; }
    setMBusy(true);
    try {
      const r = await api.post('/catalog/merge', { target: parent, sources });
      toast.success(`Merged ${sources.length} into "${parent}" · ${r.data.data.garments_updated} items updated`);
      setMSel([]); setMParent(''); setMQuery('');
      await refetch(); await refetchTags();
      onDone && onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setMBusy(false); }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-card" data-testid="merge-drawer">
        <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
        <DrawerHeader><DrawerTitle>Merge categories</DrawerTitle></DrawerHeader>
        <div className="max-h-[74vh] space-y-4 overflow-y-auto px-4 pb-6">

          {/* Manual merge — pick many, define the parent to keep */}
          <div className="rounded-2xl border border-border bg-background p-3" data-testid="manual-merge">
            <p className="text-sm font-semibold">Select &amp; merge</p>
            <p className="mb-2 text-[11px] text-muted-foreground">Tick the categories to combine, then choose the parent they all merge into.</p>
            <div className="mb-2">
              <TagCombobox value={mParent} onChange={setMParent} placeholder="Parent category (keep this)" testId="manual-parent" />
            </div>
            <input value={mQuery} onChange={(e) => setMQuery(e.target.value)} placeholder="Search categories…"
              className="mb-2 h-10 w-full rounded-lg border border-border bg-transparent px-3 text-sm outline-none" data-testid="manual-search" />
            <div className="max-h-44 space-y-1 overflow-y-auto">
              {mFiltered.map((t) => {
                const on = mSel.includes(t.name);
                return (
                  <button key={t.name} type="button" onClick={() => toggleSel(t.name)} data-testid={`manual-opt-${t.name}`}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${on ? 'border-primary bg-primary/10' : 'border-border'}`}>
                    <span className="truncate">{t.name}{t.name === mParent ? ' · parent' : ''}</span>
                    <span className={`ml-2 h-4 w-4 shrink-0 rounded border ${on ? 'border-primary bg-primary' : 'border-muted-foreground/40'}`} />
                  </button>
                );
              })}
            </div>
            <Button className="mt-2 h-10 w-full rounded-xl" onClick={manualMerge} disabled={mBusy || !mParent || mSel.filter((n) => n !== mParent).length === 0} data-testid="manual-merge-do">
              {mBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GitMerge className="mr-1.5 h-4 w-4" />Merge {mSel.filter((n) => n !== mParent).length || ''} into &ldquo;{mParent || 'parent'}&rdquo;</>}
            </Button>
          </div>

          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suggested duplicates</p>
          {isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : groups.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="merge-empty">No duplicate-looking names found.</p>
          ) : groups.map((g, idx) => {
            const target = targets[idx] || g.suggested;
            return (
              <div key={idx} className="rounded-2xl border border-border bg-background p-3" data-testid={`merge-group-${idx}`}>
                <p className="mb-2 text-xs font-medium text-muted-foreground">Keep one name — the rest merge into it:</p>
                <div className="space-y-1">
                  {g.names.map((n) => (
                    <button key={n.name} type="button" onClick={() => setTargets({ ...targets, [idx]: n.name })}
                      className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${target === n.name ? 'border-primary bg-primary/10' : 'border-border'}`}
                      data-testid={`merge-pick-${idx}-${n.name}`}>
                      <span className="truncate">{n.name}</span>
                      <span className="ml-2 shrink-0 text-[11px] text-muted-foreground">{n.count} item{n.count === 1 ? '' : 's'}{target === n.name ? ' · keep' : ''}</span>
                    </button>
                  ))}
                </div>
                <Button className="mt-2 h-10 w-full rounded-xl" onClick={() => merge(g, idx)} disabled={busyIdx === idx} data-testid={`merge-do-${idx}`}>
                  {busyIdx === idx ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GitMerge className="mr-1.5 h-4 w-4" />Merge into &ldquo;{target}&rdquo;</>}
                </Button>
              </div>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
